// apps/web/src/features/call-center/CallCenterIntakeLayout.tsx
//
// WHY (#1259): external call-center agents are confined to this page and
// must NOT see the internal AppShell (sidebar, nav, CRM data). This is a
// deliberately minimal standalone chrome — a header row carrying only the
// logout control — built locally instead of reusing AppShell precisely
// because the AppShell is the surface this boundary excludes. The real
// security boundary is the server (#1258); this layout is UX confinement.
//
// WHY (#1398): two variants share the SAME confinement chrome (no AppShell,
// no sidebar, logout-only header):
//   - `card` (default): a centered 520px card for the terminal no-access /
//     legacy form-card surfaces.
//   - `wide`: a full-width workspace shell that hosts the read-only my-leads
//     table + mobile cards at china-rates width. Both keep the same boundary.
import type { ReactNode } from 'react';

export interface CallCenterIntakeLayoutProps {
  header: ReactNode;
  children: ReactNode;
  /** `card` = 520px centered card (default); `wide` = full-width workspace. */
  variant?: 'card' | 'wide';
}

export function CallCenterIntakeLayout({
  header,
  children,
  variant = 'card',
}: CallCenterIntakeLayoutProps) {
  if (variant === 'wide') {
    return (
      <div
        style={{
          minHeight: '100vh',
          height: '100vh',
          background: 'var(--bg-2)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: 'var(--s-3) var(--s-5)',
            flex: 'none',
          }}
        >
          {header}
        </div>
        <div
          style={{
            // WHY: `position: relative` so the absolutely-positioned overlay
            //   Drawer (new-lead / detail) fills this confined content region
            //   rather than the whole viewport — same pattern as the requests
            //   workspace page container.
            position: 'relative',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-1)',
            borderTop: '1px solid var(--border-1)',
          }}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg-2)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          width: '100%',
          // WHY: no design token exists for this content max-width; AuthLayout
          // keeps its standalone-card width as a numeric literal too.
          maxWidth: 520,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--s-5)',
          padding: 'var(--s-7) var(--s-5)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{header}</div>
        <div
          style={{
            background: 'var(--bg-1)',
            border: '1px solid var(--border-1)',
            borderRadius: 'var(--r-3)',
            boxShadow: 'var(--shadow-1)',
            padding: 'var(--s-7)',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
